import styles from "./StockStamps.module.scss";
import { FC, ReactElement, ReactNode, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { getStockStamps } from "@/controllers/stock-stamps";
import { StockStamps as StockStampsModel } from "@/models/stock-stamps";
import Analysts from "@/components/analysts/Analysts";
import Dividend from "@/components/dividend/Dividend";
import EarningsPerShare from "@/components/earnings-per-share/EarningsPerShare";
import Events from "@/components/events/Events";
import Insiders from "@/components/insiders/Insiders";
import Shorts from "@/components/shorts/Shorts";
import TradingSignals from "@/components/trading-signals/TradingSignals";
import Valuation from "@/components/valuation/Valuation";
import Volume from "@/components/volume/Volume";
import { useRouter } from "next/router";
import StockStampLink from "../stock-stamp-link/StockStampLink";
import { Stock } from "@/models/stock";
import Skeleton from "../skeleton/Skeleton";
import { Box, Skeleton as MUISkeleton } from "@mui/material";
import { useNearViewport } from "src/hooks/useNearViewport";
import StockScore from "../stock-score/StockScore";
import Peers from "../peers/Peers";
// import Cds from "../cds/Cds";
import Options from "../options/Options";
import SqueezePotential from "../squeeze-potential/SqueezePotential";
import { getSqueezeScore, SqueezeScoreResponse } from "@/controllers/options-platform";

// Stamps are drawn a couple at a time once their data is in: each holds a chart, and all of them in
// one go was one of the stock page's longest tasks on a phone (it held up the user's taps). They
// fill in behind the same skeletons as while loading. Each slot reveals itself, so a render draws
// only its own stamp, not the ones already shown.
//
// A slot is drawn once it is within 300 px of the visible part of the page, and only after the
// slot two places before it (one row up on a phone) is drawn: a skeleton is much shorter than the
// stamp that replaces it, so a skeleton's position says where the stamp will be only once the stamps
// above it are drawn. On a phone the stamps are two columns of seven below the overview: the rows in
// or near view fill in at start-up, two stamps a frame, and the rest as the reader scrolls towards
// them; until then they were all built during start-up. On larger screens the stamps are in view and
// fill in two a frame, as before (ortex-gui#1349).
const STAMPS_PER_FRAME = 2;

/** How many slots, counted from the first, may start watching for the viewport. Shared without re-rendering the grid. */
const createFrontier = () => {
    let value = STAMPS_PER_FRAME;
    const listeners = new Set<() => void>();
    return {
        get: () => value,
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        /** Slot `order` is drawn: the slots up to one row below it may watch. */
        drawn: (order: number) => {
            const next = Math.max(value, order + 1 + STAMPS_PER_FRAME);
            if (next === value) return;
            value = next;
            listeners.forEach((listener) => listener());
        },
    };
};
type Frontier = ReturnType<typeof createFrontier>;
const initialFrontier = () => STAMPS_PER_FRAME;

const StampSlot: FC<{ order: number; frontier: Frontier; children: ReactNode }> = ({ order, frontier, children }) => {
    const placeholder = useRef<HTMLSpanElement>(null);
    const watching = order < useSyncExternalStore(frontier.subscribe, frontier.get, initialFrontier);
    const near = useNearViewport(placeholder, { enabled: watching });
    useEffect(() => {
        if (near) frontier.drawn(order);
    }, [near, frontier, order]);
    return near ? <>{children}</> : <MUISkeleton ref={placeholder} variant="rounded" width="100%" height="100%" />;
};

interface PropTypes {
    id: number;
    exchange: string;
    ticker: string;
    stock?: Stock;
    isETF?: boolean;
}

const StockStamps: FC<PropTypes> = ({ id, exchange, ticker, stock, isETF }) => {
    const [data, setData] = useState<StockStampsModel>();
    const [obfuscated, setObfuscated] = useState(false);
    const { pathname } = useRouter();
    const [isLoading, setIsLoading] = useState(true);
    const [tsData, setTsData] = useState<any>({});
    const [stampsAmount, setStampsAmount] = useState(0);
    // A new frontier for each set of stamps (a different stock), so they are placed afresh.
    const frontier = useMemo(() => createFrontier(), [data]);
    useEffect(() => {
        if (data !== undefined && Object.entries(data).length > 0) {
            setData(undefined);
            setStampsAmount(0);
            setIsLoading(true);
            setTimeout(() => {
                getStockStamps(stock ? stock.stock_id : id).then((res) => {
                    setObfuscated(res.obfuscated);
                    setData(res);
                    setTsData(res.last_signal_v2);
                    setIsLoading(false);
                });
            }, 500);
        } else {
            getStockStamps(stock ? stock.stock_id : id).then((res) => {
                setObfuscated(res.obfuscated);
                setData(res);
                setTsData(res.last_signal_v2);
                setIsLoading(false);
            });
        }
    }, [stock ? stock.stock_id : id]);

    const link = pathname.includes("/s/") ? `/s/${stock ? stock.exchangesymbol : exchange}/${stock ? stock.ticker : ticker}` : `/id/${stock ? stock.stock_id : id}`;

    // Squeeze Potential Score — separate nightly-computed endpoint, so it
    // fetches independently of getStockStamps. Needs a tradingitem id
    // (only the full Stock prop carries one) and only US stocks have
    // options coverage; missing/erroring quietly drops the stamp.
    // Tier gating is server-side: below Advanced the API returns a
    // band-only teaser and the stamp renders locked.
    const [squeeze, setSqueeze] = useState<SqueezeScoreResponse | null>(null);
    useEffect(() => {
        const tiId = stock?.tradingitem_id;
        if (!tiId || isETF) {
            setSqueeze(null);
            return;
        }
        let cancelled = false;
        getSqueezeScore(tiId, 90)
            .then((res) => {
                if (!cancelled) setSqueeze(res);
            })
            .catch(() => {
                if (!cancelled) setSqueeze(null);
            });
        return () => {
            cancelled = true;
        };
    }, [stock?.tradingitem_id, isETF]);

    const hasSqueezeStamp = !!(squeeze && (squeeze.latest || (squeeze.gated && squeeze.band)));

    useEffect(() => {
        if (data !== undefined) {
            const hasOptionsStamp = !!(data.option_pcr && data.option_pcr?.PCR_oi_current);

            const optionalStampsCount = [
                hasOptionsStamp,
                hasSqueezeStamp,
                // hasCdsStamp,
            ].filter(Boolean).length;

            setStampsAmount(optionalStampsCount);
        }
    }, [data, hasSqueezeStamp]);

    const shortsStamp = (
        <StockStampLink obfuscated={obfuscated} link={link + "/short-interest"} key={"shortsStamp"}>
            {data && <Shorts shortFFNow={data.short_ff_now} shortChangeRaw={data.short_change_raw} shortChangePcRaw={data?.short_change_pc_raw} shortsTs={data?.shorts_ts} overviewPage obfuscated={obfuscated} />}
        </StockStampLink>
    );

    const peersStamp = !isETF ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/peers"} key={"peersStamp"}>
            {data && <Peers ts={data.peers?.diff_ts} month={parseFloat(data.peers?.outperform_peers)} overviewPage obfuscated={obfuscated} />}
        </StockStampLink>
    ) : null;

    const insidersStamp = !isETF ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/insiders"} key={"insidersStamp"}>
            {data && <Insiders valueSold={data.insiders?.value_sold_eur} valueBought={data.insiders?.value_bought_eur} oimBarPosition={data.insiders?.OIM_bar_position} overviewPage />}
        </StockStampLink>
    ) : null;

    const analystsStamp = !isETF ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/analysts"} key={"analystsStamp"}>
            {data && (
                <Analysts upgrades={data[stock ? stock.stock_id : id]?.upgrades} downgrades={data[stock ? stock.stock_id : id]?.downgrades} buy={data.analysts?.noof_buy} hold={data.analysts?.noof_hold} sell={data.analysts?.noof_sell} overviewPage />
            )}
        </StockStampLink>
    ) : null;

    const eventsStamp = !isETF ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/events"} key={"eventsStamp"}>
            {data && <Events eventsUpcoming={data?.events_upcoming_json} eventsPrevious={data.events_previous_json} overviewPage />}
        </StockStampLink>
    ) : null;

    const stockScoreStamp = !isETF ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/stock-scores"} key={"stockScoreStamp"}>
            {data && <StockScore total={data?.stock_scores?.total} overviewPage />}
        </StockStampLink>
    ) : null;

    const epsStamp = !isETF ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/eps"} key={"epsStamp"}>
            {data && <EarningsPerShare epsChangeWeek={data.eps_change_week} epsChangeWeekCss={data.eps_change_week_css} epsTs={data?.eps_ts} overviewPage obfuscated={obfuscated} />}
        </StockStampLink>
    ) : null;

    const tradingSignalsStamp = !isETF ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/trading-signals"} key={"tradingSignalsStamp"}>
            {data && (
                <TradingSignals
                    date={tsData?.start_date !== undefined ? tsData?.start_date : tsData?.date}
                    significance={tsData?.significance}
                    direction={tsData?.["b/s"] !== undefined ? tsData?.["b/s"] : tsData?.direction}
                    absMeanPriceChange={tsData?.return !== undefined ? tsData?.return : tsData?.abs_mean_price_change}
                    triggerType={tsData?.type !== undefined ? tsData?.type : tsData?.trigger_type}
                    overviewPage
                />
            )}
        </StockStampLink>
    ) : null;

    const dividendStamp = !isETF ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/dividend"} key={"dividendStamp"}>
            {data && <Dividend pointValue={data.bar_data?.yield_vs_ind.point_value} allValues={data.bar_data?.yield_vs_ind.all_values} exdate={data.dividends?.exdate} overviewPage />}
        </StockStampLink>
    ) : null;

    const volumeStamp = (
        <StockStampLink obfuscated={obfuscated} key={"volumeStamp"}>
            {data && <Volume volumeAvg={data.volume_3m_avg} volumeTs={data.volume_ts} overviewPage obfuscated={obfuscated} />}
        </StockStampLink>
    );

    const valuationStamp = !isETF ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/fundamentals"} key={"valuationStamp"}>
            {data && (
                <Valuation
                    value={data.bar_data?.valuation_vs_ind.point_value}
                    vsIndustry={data.bar_data?.valuation_vs_ind.all_values}
                    vsHistory={data.bar_data?.valuation_vs_5y_history.all_values}
                    type={data.calculated_values?.valuation_12mfwd?.valuation_type}
                    overviewPage
                />
            )}
        </StockStampLink>
    ) : null;

    const optionsStamp =
        data && data.option_pcr && data.option_pcr?.PCR_oi_current ? (
            <StockStampLink obfuscated={obfuscated} link={link + "/options"} key={"optionsStamp"}>
                {data && <Options month={data.option_pcr.PCR_oi_current} ts={data.option_pcr_oi_ts.PCR} valueCss={""} plotLines={data.option_pcr_oi_ts["1y_50_pct"]} overviewPage obfuscated={obfuscated} />}
            </StockStampLink>
        ) : null;

    const squeezeStamp = hasSqueezeStamp ? (
        <StockStampLink obfuscated={obfuscated} link={link + "/options-platform?tab=gamma-squeeze"} key={"squeezeStamp"}>
            {data && squeeze && (
                <SqueezePotential
                    score={squeeze.latest?.score ?? null}
                    band={(squeeze.latest?.band ?? squeeze.band ?? "low") as string}
                    shortScore={squeeze.latest?.short_score}
                    optionsScore={squeeze.latest?.options_score}
                    ts={squeeze.history.slice(-63).map(([, v]) => v)}
                    gated={squeeze.gated}
                    overviewPage
                    obfuscated={obfuscated}
                />
            )}
        </StockStampLink>
    ) : null;

    // const cdsStamp = !isETF ? (
    //     data && data.cds && data.cds?.y2_change ? (
    //         <StockStampLink obfuscated={obfuscated} link={link + "/cds"} key={"cdsStamp"}>
    //             {data && <Cds change={data.cds.y2_change} latest={data.cds.y2_latest} overviewPage />}
    //         </StockStampLink>
    //     ) : null
    // ) : null;

    const stamps = [shortsStamp, peersStamp, insidersStamp, analystsStamp, eventsStamp, stockScoreStamp, epsStamp, tradingSignalsStamp, dividendStamp, volumeStamp, valuationStamp, optionsStamp, squeezeStamp];

    const getTabletGridColumns = () => {
        if (stampsAmount >= 1) return 6;
        return 5; // Default: no optional stamps
    };

    const getDesktopGridColumns = () => {
        if (isETF) return 3;
        if (stampsAmount === 3) return 14;
        if (stampsAmount === 2) return 13;
        if (stampsAmount === 1) return 12;
        return 11; // Default: no optional stamps
    };

    return (
        <Box
            className={styles.StockStamps}
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                "@media (min-width: 768px)": {
                    gridTemplateColumns: `repeat(${getTabletGridColumns()}, 1fr)`,
                    gridTemplateRows: "50% auto",
                },
                "@media (min-width: 1280px)": {
                    gridTemplateColumns: `repeat(${getDesktopGridColumns()}, 1fr)`,
                    gridTemplateRows: "100%",
                },
            }}
        >
            {isLoading
                ? stamps.map((stamp, index) => <Skeleton key={index} variant="rounded" width="100%" height="100%" loading={isLoading} />)
                : // Numbered among the stamps this stock has, so each slot has one two places before it.
                  stamps
                      .filter((stamp): stamp is ReactElement => !!stamp)
                      .map((stamp, order) => (
                          <StampSlot key={stamp.key ?? order} order={order} frontier={frontier}>
                              {stamp}
                          </StampSlot>
                      ))}
        </Box>
    );
};

export default StockStamps;
